import {
  Component,
  OnChanges,
  SimpleChanges,
  input,
  output,
  signal,
  inject,
  DestroyRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { TuiIcon } from '@taiga-ui/core';
import { MenuGroup, MenuPage, MenuSection } from '../../../core/models/menu-item.model';
import { User } from '../../../core/models/user.model';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, TuiIcon],
  template: `
    <aside class="sidebar" [class.sidebar--collapsed]="isCollapsed()">
      <!-- Marca -->
      <div class="sidebar__header">
        <div
          class="sidebar__logo"
          (click)="!isMobile() && toggleCollapse.emit()"
          [class.sidebar__logo--clickable]="!isMobile()"
        >
          @if (!isCollapsed()) {
            <div class="sidebar__logo-plate">
              <img
                class="sidebar__logo-img"
                src="/img/logo_reprico.webp"
                alt="Reprico"
                width="374"
                height="99"
              />
            </div>
          } @else {
            <div class="sidebar__logo-mark">R</div>
          }
        </div>
        <!-- @if (!isCollapsed() && !isMobile()) {
          <button
            type="button"
            class="sidebar__collapse"
            (click)="toggleCollapse.emit()"
            aria-label="Contraer menú"
          >
            <tui-icon icon="@tui.panel-left-close" />
          </button>
        } -->
      </div>

      <!-- Navegación -->
      <nav class="sidebar__nav">
        <!--
          Acceso al inicio. No viene del menú del API ni pertenece a ningún
          módulo: es la pantalla de entrada, así que va fijo arriba de todo.
        -->
        <a
          routerLink="/dashboard"
          class="sidebar__home"
          [class.sidebar__home--rail]="isCollapsed()"
          routerLinkActive="sidebar__home--active"
          [routerLinkActiveOptions]="{ exact: true }"
          [attr.title]="isCollapsed() ? 'Inicio' : null"
          (click)="handleHomeClick()"
        >
          <tui-icon class="sidebar__home-icon" icon="@tui.house" />
          @if (!isCollapsed()) {
            <span class="sidebar__home-text">Inicio</span>
          }
        </a>

        @for (group of menuGroups(); track group.id) {
          <div class="sidebar__group">
            @if (!isCollapsed()) {
              <button
                type="button"
                class="sidebar__group-header"
                [class.sidebar__group-header--open]="isGroupExpanded(group.id)"
                (click)="toggleGroup(group.id)"
              >
                <tui-icon class="sidebar__group-icon" [icon]="resolveIcon(group.icon, group.label)" />
                <span class="sidebar__group-title">{{ group.label }}</span>
                <tui-icon
                  class="sidebar__group-caret"
                  [class.sidebar__group-caret--open]="isGroupExpanded(group.id)"
                  icon="@tui.chevron-down"
                />
              </button>
            } @else {
              <div class="sidebar__divider"></div>
            }

            @if (!isCollapsed() && isGroupExpanded(group.id)) {
              @for (menuSection of group.sections; track menuSection.id) {
                <div
                  class="sidebar__section"
                  [class.sidebar__section--open]="isSectionExpanded(menuSection.id)"
                >
                  <button
                    type="button"
                    class="sidebar__section-header"
                    [class.sidebar__section-header--active]="isSectionActive(menuSection)"
                    [attr.aria-expanded]="isSectionExpanded(menuSection.id)"
                    (click)="toggleSection(menuSection.id)"
                  >
                    <tui-icon
                      class="sidebar__section-caret"
                      [class.sidebar__section-caret--open]="isSectionExpanded(menuSection.id)"
                      icon="@tui.chevron-right"
                    />
                    <span class="sidebar__section-label">{{ menuSection.label }}</span>
                    <span class="sidebar__section-count">{{ menuSection.pages.length }}</span>
                  </button>

                  @if (isSectionExpanded(menuSection.id)) {
                  <ul class="sidebar__menu">
                    @for (page of menuSection.pages; track page.id + '-' + page.label) {
                      @if (page.children && page.children.length) {
                        <li
                          class="sidebar__item sidebar__item--parent"
                          [class.sidebar__item--open]="isPageExpanded(page.id)"
                        >
                          <button
                            type="button"
                            class="sidebar__link sidebar__link--parent"
                            [class.sidebar__link--active]="isParentActive(page)"
                            [attr.aria-expanded]="isPageExpanded(page.id)"
                            (click)="togglePage(page.id, $event)"
                          >
                            <tui-icon
                              class="sidebar__link-icon"
                              [icon]="resolveIcon(page.icon, page.label, page.routerLink)"
                            />
                            <span class="sidebar__link-text">{{ page.label }}</span>
                            <span class="sidebar__link-count">{{ page.children.length }}</span>
                            <tui-icon
                              class="sidebar__link-caret"
                              [class.sidebar__link-caret--open]="isPageExpanded(page.id)"
                              icon="@tui.chevron-down"
                            />
                          </button>
                          <ul class="sidebar__submenu">
                            @for (child of page.children; track child.id + '-' + child.label) {
                              <li>
                                <a
                                  [routerLink]="child.routerLink"
                                  [queryParams]="child.queryParams || null"
                                  class="sidebar__sublink"
                                  [class.sidebar__sublink--active]="isChildActive(child)"
                                  [class.sidebar__link--disabled]="child.disabled"
                                  (click)="handleItemClick(child, $event)"
                                >
                                  <span class="sidebar__sublink-dot"></span>
                                  <span class="sidebar__link-text">{{ child.label }}</span>
                                </a>
                              </li>
                            }
                          </ul>
                        </li>
                      } @else {
                        <li class="sidebar__item">
                          <a
                            [routerLink]="page.routerLink"
                            [queryParams]="page.queryParams || null"
                            class="sidebar__link"
                            routerLinkActive="sidebar__link--active"
                            [routerLinkActiveOptions]="{ exact: false }"
                            [class.sidebar__link--disabled]="page.disabled"
                            (click)="handleItemClick(page, $event)"
                          >
                            <!--
                              Viñeta en lugar de icono: las páginas ya cuelgan
                              de una sección, y una fila de iconos distintos
                              competía con los del primer nivel.
                            -->
                            <span class="sidebar__link-bullet"></span>
                            <span class="sidebar__link-text">{{ page.label }}</span>
                          </a>
                        </li>
                      }
                    }
                  </ul>
                  }
                </div>
              }
            } @else if (isCollapsed()) {
              <ul class="sidebar__menu sidebar__menu--rail">
                @for (menuSection of group.sections; track menuSection.id) {
                  @for (page of menuSection.pages; track page.id + '-' + page.label) {
                    @if (page.children && page.children.length) {
                      @for (child of page.children; track child.id + '-' + child.label) {
                        <li>
                          <a
                            [routerLink]="child.routerLink"
                            [queryParams]="child.queryParams || null"
                            class="sidebar__link sidebar__link--rail"
                            [class.sidebar__link--active]="isChildActive(child)"
                            [class.sidebar__link--disabled]="child.disabled"
                            [attr.title]="group.label + ' / ' + page.label + ' / ' + child.label"
                            (click)="handleItemClick(child, $event)"
                          >
                            <tui-icon
                              class="sidebar__link-icon"
                              [icon]="resolveIcon(child.icon, child.label, child.routerLink)"
                            />
                          </a>
                        </li>
                      }
                    } @else {
                      <li>
                        <a
                          [routerLink]="page.routerLink"
                          [queryParams]="page.queryParams || null"
                          class="sidebar__link sidebar__link--rail"
                          routerLinkActive="sidebar__link--active"
                          [routerLinkActiveOptions]="{ exact: false }"
                          [class.sidebar__link--disabled]="page.disabled"
                          [attr.title]="group.label + ' / ' + menuSection.label + ' / ' + page.label"
                          (click)="handleItemClick(page, $event)"
                        >
                          <tui-icon
                            class="sidebar__link-icon"
                            [icon]="resolveIcon(page.icon, page.label, page.routerLink)"
                          />
                        </a>
                      </li>
                    }
                  }
                }
              </ul>
            }
          </div>
        }
      </nav>

      <!-- Pie -->
      <div class="sidebar__footer">
        @if (isCollapsed() && !isMobile()) {
          <button
            type="button"
            class="sidebar__expand"
            (click)="toggleCollapse.emit()"
            aria-label="Expandir menú"
          >
            <tui-icon icon="@tui.panel-left-open" />
          </button>
        } @else {
          <span class="sidebar__version">v{{ appVersion }}</span>
        }
      </div>
    </aside>
  `,
  styleUrl: './sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnChanges {
  readonly appVersion = environment.appVersion;
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  collapsed = input<boolean>(false);
  menuGroups = input<MenuGroup[]>([]);
  currentUser = input<User | null>(null);
  toggleCollapse = output<void>();
  itemSelected = output<void>();

  activeGroupId: number | null = null;
  readonly expandedPages = signal<Set<number>>(new Set<number>());
  /** Secciones desplegadas dentro del módulo abierto. */
  readonly expandedSections = signal<Set<number>>(new Set<number>());
  private readonly userToggled = new Set<number>();
  /** Secciones que el usuario abrió o cerró a mano, para no reabrirlas solas. */
  private readonly userToggledSections = new Set<number>();
  readonly currentUrl = signal<string>('');

  constructor() {
    this.currentUrl.set(this.router.url);
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        this.currentUrl.set(e.urlAfterRedirects);
        this.expandActiveParents();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['menuGroups']) {
      const groups = this.menuGroups();
      if (this.activeGroupId === null && groups.length > 0) {
        this.activeGroupId = groups[0].id;
      }
      this.expandActiveParents();
    }
  }

  isGroupExpanded(groupId: number): boolean {
    return this.activeGroupId === groupId;
  }

  toggleGroup(groupId: number): void {
    this.activeGroupId = this.activeGroupId === groupId ? null : groupId;
  }

  isSectionExpanded(sectionId: number): boolean {
    return this.expandedSections().has(sectionId);
  }

  toggleSection(sectionId: number): void {
    this.userToggledSections.add(sectionId);

    const next = new Set(this.expandedSections());
    if (next.has(sectionId)) {
        next.delete(sectionId);
    } else {
        next.add(sectionId);
    }
    this.expandedSections.set(next);
  }

  /** `true` si alguna página de la sección corresponde a la ruta actual. */
  isSectionActive(section: MenuSection): boolean {
    return section.pages.some(
      (page) => this.isChildActive(page) || this.isParentActive(page),
    );
  }

  isPageExpanded(pageId: number): boolean {
    return this.expandedPages().has(pageId);
  }

  togglePage(pageId: number, event?: Event): void {
    event?.stopPropagation();
    this.userToggled.add(pageId);
    const next = new Set(this.expandedPages());
    if (next.has(pageId)) {
        next.delete(pageId);
    } else {
        next.add(pageId);
    }
    this.expandedPages.set(next);
  }

  private expandActiveParents(): void {
    this.expandActiveSections();

    const next = new Set(this.expandedPages());
    let changed = false;
    for (const group of this.menuGroups()) {
      for (const section of group.sections) {
        for (const page of section.pages) {
          if (
            page.children?.length &&
            this.isParentActive(page) &&
            !next.has(page.id) &&
            !this.userToggled.has(page.id)
          ) {
            next.add(page.id);
            changed = true;
          }
        }
      }
    }
    if (changed) {
      this.expandedPages.set(next);
    }
  }

  /**
   * Despliega la sección que contiene la página actual y, si el módulo tiene
   * una sola sección, la deja abierta: no aporta nada tener que abrirla.
   */
  private expandActiveSections(): void {
    const next = new Set(this.expandedSections());
    let changed = false;

    for (const group of this.menuGroups()) {
      const unicaSeccion = group.sections.length === 1;

      for (const section of group.sections) {
        const shouldOpen = unicaSeccion || this.isSectionActive(section);

        if (shouldOpen && !next.has(section.id) && !this.userToggledSections.has(section.id)) {
          next.add(section.id);
          changed = true;
        }
      }
    }

    if (changed) {
      this.expandedSections.set(next);
    }
  }

  isParentActive(page: MenuPage): boolean {
    return (page.children ?? []).some((child) => this.isChildActive(child));
  }

  isChildActive(child: MenuPage): boolean {
    if (!child.routerLink) {
      return false;
    }
    const [urlPath, urlQuery = ''] = this.currentUrl().split('?');
    const childPath = child.routerLink.split('?')[0];
    const pathMatches = urlPath === childPath || urlPath.startsWith(`${childPath}/`);
    if (!pathMatches) {
      return false;
    }
    if (!child.queryParams) {
      return true;
    }
    const params = new URLSearchParams(urlQuery);
    return Object.entries(child.queryParams).every(([key, value]) => params.get(key) === String(value));
  }

  isCollapsed(): boolean {
    if (this.isMobile()) return false;
    return this.collapsed();
  }

  isMobile(): boolean {
    return typeof window !== 'undefined' && window.innerWidth <= 768;
  }

  /** En móvil el menú se cierra al elegir, igual que con el resto de enlaces. */
  handleHomeClick(): void {
    if (this.isMobile()) {
      this.itemSelected.emit();
    }
  }

  handleItemClick(page: MenuPage, event: Event): void {
    if (page.disabled || !page.routerLink) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (this.isMobile()) {
      this.itemSelected.emit();
    }
  }

  /**
   * Resuelve el icono de Taiga a mostrar. La API entrega iconos con nomenclatura
   * PrimeNG (`pi pi-*`), así que se traducen y, si no hay coincidencia, se
   * infiere por el nombre de la página o su ruta.
   */
  resolveIcon(rawIcon: string | undefined, label = '', routerLink = ''): string {
    const mapped = this.mapLegacyIcon(rawIcon);
    if (mapped) {
      return mapped;
    }

    const key = `${label} ${routerLink}`.toLowerCase();

    if (key.includes('notificacion')) return '@tui.bell';
    if (key.includes('visita')) return '@tui.calendar';
    if (key.includes('promocion')) return '@tui.megaphone';
    if (key.includes('kpi')) return '@tui.chart-pie';
    if (key.includes('mapa')) return '@tui.map-pin';
    if (key.includes('reporte')) return '@tui.chart-line';
    if (key.includes('power sku')) return '@tui.zap';
    if (key.includes('sincronizador')) return '@tui.refresh-cw';
    if (key.includes('catalogo') || key.includes('catálogo')) return '@tui.boxes';
    if (key.includes('registro')) return '@tui.building-2';
    if (key.includes('flujo')) return '@tui.arrow-left-right';
    if (key.includes('mensual')) return '@tui.chart-line';
    if (key.includes('balance')) return '@tui.wallet';
    if (key.includes('usuario')) return '@tui.users';
    if (key.includes('rol')) return '@tui.shield';
    if (key.includes('datos maestros')) return '@tui.database';
    if (key.includes('pagina') || key.includes('página')) return '@tui.file-text';
    if (key.includes('dashboard') || key.includes('inicio')) return '@tui.layout-dashboard';
    if (key.includes('administracion') || key.includes('administración')) return '@tui.settings';

    return '@tui.circle';
  }

  private mapLegacyIcon(rawIcon: string | undefined): string {
    if (!rawIcon) {
      return '';
    }

    if (rawIcon.startsWith('@tui.')) {
      return rawIcon;
    }

    const name = rawIcon.replace(/pi\s+|pi-fw\s*/g, '').replace('pi-', '').trim();

    const legacyMap: Record<string, string> = {
      users: '@tui.users',
      user: '@tui.user',
      shield: '@tui.shield',
      cog: '@tui.settings',
      file: '@tui.file-text',
      database: '@tui.database',
      box: '@tui.boxes',
      bell: '@tui.bell',
      calendar: '@tui.calendar',
      megaphone: '@tui.megaphone',
      'chart-pie': '@tui.chart-pie',
      'chart-line': '@tui.chart-line',
      'map-marker': '@tui.map-pin',
      bolt: '@tui.zap',
      sync: '@tui.refresh-cw',
      'building-columns': '@tui.building-2',
      'arrows-h': '@tui.arrow-left-right',
      wallet: '@tui.wallet',
      'th-large': '@tui.layout-dashboard',
    };

    return legacyMap[name] ?? '';
  }
}
